import React, { useEffect, useState } from 'react';

import { Dropdown } from '@missionlabs/smartagent-app-components'
import { SADropdownOption, SADropdownProps } from '@missionlabs/smartagent-app-components/dist/Dropdown'
import { useSelector, useDispatch } from 'react-redux'
import RootState from 'store/state'
import { getEnabledQueues, getQueues } from 'store/settings/settings.actions'
import { AppFeatures, useCompanyHasFeature } from 'hooks/useHasFeature';


interface Props extends Omit<SADropdownProps, "options" | "onChange"> {
    onSelect: (queue:Queue) => void;
    selectedQueueID?: string;
    blankOption?: boolean;
    filterFn?: (agent:Queue)=>boolean;
    label?: string;
    title?: string;
    shouldUseTaggedQueues?: boolean;
}

export interface Queue{
    ID:string;
    name:string;
    arn: string;
}

const QueueDropdown: React.FC<Props> = ({ selectedQueueID, onSelect, blankOption, filterFn, label, title, shouldUseTaggedQueues, ...props}) => {
    const dispatch = useDispatch();
    const companyHasFeature = useCompanyHasFeature()

    const userTags = useSelector((state: RootState) => state.user?.tags)

    const hasRestrictQueueAccessFeature = companyHasFeature(AppFeatures.RESTRICT_QUEUE_ACCESS)

    const enabledQueues = useSelector<RootState, RootState['settings']['enabledQueues']>(
        (state) => state.settings?.enabledQueues || [],
    )

    const companyQueues = useSelector<RootState, RootState['settings']['queues']>(
        (state) => state.settings?.queues || [],
    )

    const queues = React.useMemo(() => {
        return hasRestrictQueueAccessFeature
            ? userTags
                ? enabledQueues
                : []
            : companyQueues
    }, [hasRestrictQueueAccessFeature, userTags, enabledQueues])

    useEffect(() => {
        if (!queues?.length) {
            if (!hasRestrictQueueAccessFeature) {
                dispatch(getQueues())
            }
            if (userTags && hasRestrictQueueAccessFeature && shouldUseTaggedQueues) {
                dispatch(getEnabledQueues(userTags))
            }
        }
    }, [queues?.length, dispatch])

    const selected = queues.find(a=>a.Id === selectedQueueID)

    const options:SADropdownOption[] = queues?.sort((a,b)=>a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1).map(q=>({
        label: q.Name,
        data: {
            ID: q.Id,
            name: q.Name,
            arn: q.Arn
        }
    })) ?? [];

    if(!queues.length){
        options.unshift({
            label:'Loading Queues...',
            data:undefined
        })
    }
    else if(blankOption){
        options.unshift({
            label:'',
            data:undefined
        })
    }

    return (
        <Dropdown
            {...props}
            options={filterFn ? options.filter(option=>filterFn(option.data)) : options}
            value={props.value ?? selected?.Name ?? ''}
            title={title ?? "Select queue"}
            label={label ? label : 'Queue'}
            onChange={(value: SADropdownOption<Queue>) => onSelect(value.data)}
        />
    )
}

export default QueueDropdown;